/**
 * NPCListView
 *
 * Displays campaign NPCs loaded from Supabase in the GM Assistant popup.
 * Groups by keystone status, shows disposition, role, dialogue hints, GM notes.
 */

import { useEffect } from 'react';
import { useNpcStore } from '../../stores/npcStore';
import { Icon } from '../Icon';
import type { CampaignNpc } from '@machina/core';

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.4)';
const SILVER = '#c0c0c0';

function fs(base: number, scale: number) { return base + scale; }

interface Props {
  campaignId: string;
  fontScale: number;
}

export function NPCListView({ campaignId, fontScale }: Props) {
  const npcs = useNpcStore((s) => s.npcs);
  const isLoading = useNpcStore((s) => s.isLoading);
  const loadNpcs = useNpcStore((s) => s.loadNpcs);
  const subscribeToRealtime = useNpcStore((s) => s.subscribeToRealtime);
  const unsubscribeFromRealtime = useNpcStore((s) => s.unsubscribeFromRealtime);
  const f = (base: number) => fs(base, fontScale);

  useEffect(() => {
    loadNpcs(campaignId);
    subscribeToRealtime(campaignId);
    return () => unsubscribeFromRealtime();
  }, [campaignId, loadNpcs, subscribeToRealtime, unsubscribeFromRealtime]);

  if (isLoading && npcs.length === 0) {
    return (
      <div style={styles.empty}>
        <Icon name="loader" size={24} color={GOLD_DIM} />
        <div style={{ color: GOLD_DIM, fontSize: f(12) }}>Loading NPCs...</div>
      </div>
    );
  }

  if (npcs.length === 0) {
    return (
      <div style={styles.empty}>
        <Icon name="users" size={28} color={GOLD_DIM} />
        <div style={{ color: GOLD_DIM, fontSize: f(13), fontStyle: 'italic' }}>
          No NPCs yet
        </div>
        <div style={{ color: 'rgba(192, 192, 192, 0.3)', fontSize: f(11), maxWidth: 240, textAlign: 'center' }}>
          Load a session or create NPCs in the GM Tray.
        </div>
      </div>
    );
  }

  const keystones = npcs.filter((n) => n.isKeystone);
  const others = npcs.filter((n) => !n.isKeystone);

  return (
    <div style={styles.container}>
      {keystones.length > 0 && (
        <>
          <div style={{ ...styles.sectionHeader, fontSize: f(9) }}>
            <Icon name="target" size={f(11)} color={GOLD} />
            <span>KEYSTONE NPCs</span>
            <div style={styles.sectionLine} />
          </div>
          <div style={styles.list}>
            {keystones.map((npc) => (
              <NPCRow key={npc.id} npc={npc} fontScale={fontScale} />
            ))}
          </div>
        </>
      )}
      {others.length > 0 && (
        <>
          <div style={{ ...styles.sectionHeader, fontSize: f(9) }}>
            <Icon name="users" size={f(11)} color={SILVER} />
            <span>OTHER NPCs</span>
            <div style={styles.sectionLine} />
          </div>
          <div style={styles.list}>
            {others.map((npc) => (
              <NPCRow key={npc.id} npc={npc} fontScale={fontScale} />
            ))}
          </div>
        </>
      )}
      <div style={{ height: 40 }} />
    </div>
  );
}

const STATUS_COLORS: Record<string, string> = {
  active: '#4caf50',
  allied: '#2196f3',
  inactive: 'rgba(192, 192, 192, 0.4)',
  missing: '#ff9800',
  dead: '#dc143c',
};

function NPCRow({ npc, fontScale }: { npc: CampaignNpc; fontScale: number }) {
  const f = (base: number) => fs(base, fontScale);
  const statusColor = STATUS_COLORS[npc.status] || SILVER;

  return (
    <div style={styles.card}>
      {/* Name + status */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span style={{ color: GOLD, fontSize: f(12), fontWeight: 'bold' }}>{npc.name}</span>
          {npc.title && (
            <span style={{ color: 'rgba(192, 192, 192, 0.5)', fontSize: f(10), fontStyle: 'italic' }}>
              {npc.title}
            </span>
          )}
        </div>
        <span style={{ color: statusColor, fontSize: f(9), letterSpacing: 0.5, textTransform: 'uppercase' }}>
          {npc.status}
        </span>
      </div>

      {/* Role + disposition */}
      {(npc.role || npc.disposition) && (
        <div style={{ display: 'flex', gap: 12, marginTop: 3 }}>
          {npc.role && (
            <span style={{ color: GOLD_DIM, fontSize: f(10) }}>{npc.role}</span>
          )}
          {npc.disposition && (
            <span style={{ color: 'rgba(192, 192, 192, 0.5)', fontSize: f(10) }}>{npc.disposition}</span>
          )}
        </div>
      )}

      {/* Description */}
      {npc.description && (
        <div style={{ color: 'rgba(224, 220, 208, 0.6)', fontSize: f(10), marginTop: 6, lineHeight: 1.5 }}>
          {npc.description}
        </div>
      )}

      {/* Location */}
      {npc.currentLocation && (
        <div style={{ marginTop: 4 }}>
          <span style={{ color: GOLD_DIM, fontSize: f(9), letterSpacing: 1 }}>LOCATION: </span>
          <span style={{ color: SILVER, fontSize: f(10) }}>{npc.currentLocation}</span>
        </div>
      )}

      {/* Dialogue hints */}
      {npc.dialogueHints.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ color: GOLD_DIM, fontSize: f(9), letterSpacing: 1, marginBottom: 3 }}>DIALOGUE</div>
          {npc.dialogueHints.map((hint, i) => (
            <div key={i} style={{ color: 'rgba(192, 192, 192, 0.5)', fontSize: f(10), padding: '1px 0', fontStyle: 'italic' }}>
              "{hint}"
            </div>
          ))}
        </div>
      )}

      {/* GM notes */}
      {npc.gmNotes && (
        <div style={{ marginTop: 6, padding: '6px 10px', backgroundColor: 'rgba(192, 192, 192, 0.03)', borderRadius: 2 }}>
          <div style={{ color: GOLD_DIM, fontSize: f(9), letterSpacing: 1, marginBottom: 2 }}>GM NOTES</div>
          <div style={{ color: 'rgba(192, 192, 192, 0.5)', fontSize: f(10), whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {npc.gmNotes}
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '0 16px',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    height: '100%',
    minHeight: 300,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '14px 0 6px',
    color: GOLD,
    letterSpacing: 1.5,
    fontWeight: 'bold',
  },
  sectionLine: {
    flex: 1,
    height: 1,
    backgroundColor: GOLD_DIM,
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  card: {
    padding: '10px 14px',
    border: `1px solid ${GOLD_DIM}`,
    borderRadius: 2,
    backgroundColor: 'rgba(212, 175, 55, 0.03)',
  },
};
